import { useCallback } from 'react';

import { Slug } from 'Utilities/profiling/@types';
import { isEnquiryExpirable } from 'Utilities/restricted';
import { Vehicle, VehicleLookupState } from 'Utilities/vehicles/@types';
import { EnquiryOfferResponse } from 'Utilities/vehicles/hooks/useGetEnquiryOffers';
import { useGetEnquiryStatusAndOffers } from 'Utilities/vehicles/hooks/useGetEnquiryStatusAndOffers';
import useGetVehicleEnquiry from 'Utilities/vehicles/hooks/useGetVehicleEnquiry';

import { LatestPostSaleOffer, PaymentStatuses } from '../../../payments/Payments.types';

import { checkEnquiryActive } from './useVehicleCard.helpers';
import { useVehicleCardCta } from './useVehicleCardCta';

type RenderPriceOptions = {
  fallbackValuationPrice?: number | null;
  showEstimateOnLoad?: boolean;
};

export type UseVehicleCardPropsReturn = {
  ctaProps: {
    disabled: boolean;
    label: string;
    loading: boolean;
    to: string;
  };
  estimateData: {
    enquiryOffers?: EnquiryOfferResponse | null;
    enquiryStateSlug?: Slug | null;
    latestPostSaleOfferData?: LatestPostSaleOffer | null;
    paymentsStatuses?: PaymentStatuses | null;
  };
  gaEvent?: string;
  isEnquiryActive: boolean;
  isEnquiryExpirable: boolean;
  isEnquiryExpired: boolean;
  isOfferRestricted?: boolean;
  isSellingButtonLoading: boolean;
  renderPrice: (options?: RenderPriceOptions) => number | null | undefined;
};

export const useVehicleCard = ({ isCVT, vehicle }:
  {isCVT?: boolean; vehicle?: Vehicle | VehicleLookupState}): UseVehicleCardPropsReturn => {
  const {
    enquiryOffers,
    enquiryStateSlug,
    isLoading: isEnquiryLoading,
    isOfferRestricted,
    latestPostSaleOfferData,
    paymentsStatuses,
  } = useGetEnquiryStatusAndOffers({ vehicle });

  const { enquiry: latestVehicleEnquiry, isLoaded: isLatestVehicleEnquiryLoaded } = useGetVehicleEnquiry({ vehicle });

  const hasExpirableStatus = isEnquiryExpirable(enquiryStateSlug);
  const isEnquiryActive = checkEnquiryActive(vehicle?.enquiry);
  const isEnquiryExpired = hasExpirableStatus && !isEnquiryActive;

  const isSellingButtonLoading = isEnquiryLoading || !isLatestVehicleEnquiryLoaded;

  const { gaEvent, label, link } = useVehicleCardCta({
    dpEnquiryId: vehicle?.enquiry?.dpEnquiryId,
    enquiryOffers,
    enquiryStateSlug,
    isCVT,
    isEnquiryExpired,
    latestPostSaleOfferData,
    paymentsStatuses,
    vrm: vehicle?.vrm,
  });

  const renderPrice = useCallback((options?: RenderPriceOptions) => {
    if (isLatestVehicleEnquiryLoaded) {
      return latestPostSaleOfferData?.amount
        || enquiryOffers?.closingBid?.value
        || latestVehicleEnquiry?.displayPrice
        || latestVehicleEnquiry?.sellerWillAcceptPrice
        || latestVehicleEnquiry?.priceHigh
        || options?.fallbackValuationPrice
        || vehicle?.enquiry?.valuation?.estimate;
    }

    return vehicle?.enquiry?.valuation?.estimate;
  }, [
    isLatestVehicleEnquiryLoaded,
    latestPostSaleOfferData?.amount,
    enquiryOffers?.closingBid?.value,
    latestVehicleEnquiry?.displayPrice,
    latestVehicleEnquiry?.sellerWillAcceptPrice,
    latestVehicleEnquiry?.priceHigh,
    vehicle?.enquiry?.valuation?.estimate,
  ]);

  return {
    ctaProps: {
      disabled: isSellingButtonLoading,
      label,
      loading: isSellingButtonLoading,
      to: link,
    },
    estimateData: {
      enquiryOffers,
      enquiryStateSlug,
      latestPostSaleOfferData,
      paymentsStatuses,
    },
    gaEvent,
    isEnquiryActive,
    isEnquiryExpirable: hasExpirableStatus,
    isEnquiryExpired,
    isOfferRestricted,
    isSellingButtonLoading,
    renderPrice,
  };
};
